import {
  AnimatePresence,
  motion,
} from "framer-motion";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { FaCheckCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";

import { userState } from "../recoil/atoms/userAtom";
import {
  authenticateTableUser,
  getAuthToken,
  storeAuthToken,
} from "../services/authService.ts";
import { getTable } from "../services/tableService.ts";
import {
  storeTableId,
  useTableId,
} from "../utils/tableId";

import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  updateProfile,
} from "firebase/auth";
import { auth } from "../firebase";

import MobileStep from "./login/MobileStep";
import OtpStep from "./login/OtpStep";
import RegisterStep from "./login/RegisterStep";

const STEPS = [
  "mobile",
  "otp",
  "register",
];

const StepDots = ({ step }) => (
  <div className="flex items-center justify-center gap-2 mb-6">
    {STEPS.map((currentStep, index) => (
      <motion.div
        key={currentStep}
        animate={{
          width:
            step === currentStep
              ? 20
              : 6,

          backgroundColor:
            step === currentStep
              ? "#00713b"
              : STEPS.indexOf(step) > index
                ? "#86efac"
                : "#d1d5db",
        }}
        transition={{ duration: 0.3 }}
        className="h-1.5 rounded-full"
      />
    ))}
  </div>
);

const LoginScreen = () => {
  const navigate = useNavigate();

  const [user, setUser] =
    useRecoilState(userState);

  const tableId = useTableId();

  const [tableData, setTableData] =
    useState(null);

  const [tableLoading, setTableLoading] =
    useState(true);

  const [step, setStep] =
    useState("mobile");

  const [mobile, setMobile] =
    useState("");

  const [otpInput, setOtpInput] =
    useState(["", "", "", "", "", ""]);

  const [fullName, setFullName] =
    useState("");

  const [mobileError, setMobileError] =
    useState("");

  const [otpError, setOtpError] =
    useState("");

  const [nameError, setNameError] =
    useState("");

  const [toast, setToast] =
    useState(null);

  const [sendingOTP, setSendingOTP] =
    useState(false);

  const [verifyingOTP, setVerifyingOTP] =
    useState(false);

  const [resendCountdown, setResendCountdown] =
    useState(0);

  const [expiryCountdown, setExpiryCountdown] =
    useState(300);

  const confirmationResultRef =
    useRef(null);

  const webOtpRequestRef = useRef(null);

  const stopWebOtp = useCallback(() => {
    const request = webOtpRequestRef.current;
    webOtpRequestRef.current = null;
    if (request) {
      clearTimeout(request.timer);
      request.controller.abort();
    }
  }, []);

  useEffect(() => () => stopWebOtp(), [stopWebOtp]);

  useEffect(() => {
    if (step === "otp" && expiryCountdown <= 0) stopWebOtp();
  }, [step, expiryCountdown, stopWebOtp]);

  const startWebOtp = () => {
    stopWebOtp();
    if (!window.isSecureContext || !("OTPCredential" in window) ||
        typeof navigator.credentials?.get !== "function") return;

    const request = { controller: new AbortController(), timer: null };
    webOtpRequestRef.current = request;
    request.timer = setTimeout(stopWebOtp, 300000);

    // The browser resolves this request only after the user grants consent.
    // Start listening before Firebase sends the SMS to avoid missing it.
    const receive = async () => {
      try {
        const credential = await navigator.credentials.get({
          otp: { transport: ["sms"] },
          signal: request.controller.signal,
        });
        if (webOtpRequestRef.current !== request || request.controller.signal.aborted) return;
        if (/^\d{6}$/.test(credential?.code || "")) {
          setOtpInput(credential.code.split(""));
          setOtpError("");
        }
      } catch {
        // Denial, timeout, and unsupported browsers leave manual entry available.
      } finally {
        clearTimeout(request.timer);
        if (webOtpRequestRef.current === request) webOtpRequestRef.current = null;
      }
    };
    receive();
  };

  useEffect(() => {
    if (
      user?.fullName &&
      user?.mobileNumber &&
      getAuthToken()
    ) {
      navigate("/", {
        replace: true,
      });
    }
  }, [user, navigate]);

  useEffect(() => {
    const loadTable = async () => {
      if (!tableId) {
        setTableLoading(false);
        return;
      }

      try {
        const data =
          await getTable(tableId);

        if (!data?.status) {
          throw new Error(
            "The scanned table is not available."
          );
        }

        storeTableId(tableId);
        setTableData(data);
      } catch (error) {
        console.log(
          "LoginScreen: could not load table",
          error
        );
      } finally {
        setTableLoading(false);
      }
    };

    loadTable();
  }, [tableId]);

  useEffect(() => {
    if (
      step !== "otp" ||
      resendCountdown <= 0
    ) {
      return;
    }

    const timer = setInterval(() => {
      setResendCountdown(
        (currentCount) => currentCount - 1
      );
    }, 1000);

    return () => clearInterval(timer);
  }, [step, resendCountdown]);

  useEffect(() => {
    if (
      step !== "otp" ||
      expiryCountdown <= 0
    ) {
      return;
    }

    const timer = setInterval(() => {
      setExpiryCountdown((currentCount) => {
        if (currentCount <= 1) {
          setOtpError(
            "OTP expired. Please resend."
          );

          return 0;
        }

        return currentCount - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [step, expiryCountdown]);

  useEffect(() => {
    return () => {
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (_) {
          // Ignore cleanup errors.
        }

        window.recaptchaVerifier = null;
      }
    };
  }, []);

  const showToast = ({
    type = "error",
    text1,
    text2,
  }) => {
    setToast({
      type,
      text1,
      text2,
    });

    setTimeout(() => {
      setToast(null);
    }, 4000);
  };

  const goHome = () => {
    if (tableId) {
      storeTableId(tableId);
    }

    navigate("/", {
      replace: true,
    });
  };

  const handleOtpChange = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 6);
    setOtpInput(Array.from({ length: 6 }, (_, index) => digits[index] || ""));
    setOtpError("");
  };

  const setupRecaptcha = () => {
    if (window.recaptchaVerifier) {
      return window.recaptchaVerifier;
    }

    const button = document.getElementById(
      "login-send-otp-btn"
    );

    if (!button) {
      console.warn(
        "Send OTP button not found in DOM"
      );

      return null;
    }

    window.recaptchaVerifier =
      new RecaptchaVerifier(
        auth,
        "login-send-otp-btn",
        {
          size: "invisible",

          callback: () => { },

          "expired-callback": () => {
            showToast({
              type: "error",
              text1:
                "reCAPTCHA expired. Please try again.",
            });

            if (window.recaptchaVerifier) {
              try {
                window.recaptchaVerifier.clear();
              } catch (_) {
                // Ignore cleanup errors.
              }

              window.recaptchaVerifier = null;
            }
          },
        }
      );

    return window.recaptchaVerifier;
  };

  const sendOTP = async () => {
    setMobileError("");

    if (
      !mobile.trim() ||
      !/^[6-9]\d{9}$/.test(mobile)
    ) {
      setMobileError(
        "Enter a valid 10-digit mobile number"
      );

      return;
    }

    setSendingOTP(true);
    setOtpInput(["", "", "", "", "", ""]);
    setOtpError("");

    try {
      const verifier = setupRecaptcha();

      if (!verifier) {
        throw new Error(
          "Unable to initialize security verification. Please reload."
        );
      }

      startWebOtp();
      const confirmationResult =
        await signInWithPhoneNumber(
          auth,
          "+91" + mobile,
          verifier
        );

      confirmationResultRef.current =
        confirmationResult;

      setExpiryCountdown(300);
      setResendCountdown(30);
      setStep("otp");

      showToast({
        type: "success",
        text1:
          "OTP sent! Check your SMS 📱",
      });
    } catch (error) {
      stopWebOtp();
      console.error(
        "Firebase signInWithPhoneNumber error:",
        error
      );

      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (_) {
          // Ignore cleanup errors.
        }

        window.recaptchaVerifier = null;
      }

      let errorMessage =
        "Failed to send OTP. Please try again.";

      if (
        error.code ===
        "auth/billing-not-enabled"
      ) {
        errorMessage =
          "Firebase billing not enabled for real SMS. Add a Test Phone Number in Firebase Console or switch to Blaze plan.";
      } else if (
        error.code ===
        "auth/operation-not-allowed"
      ) {
        errorMessage =
          "Phone sign-in is disabled in Firebase Console. Please enable Phone under Authentication > Sign-in method.";
      } else if (
        error.code ===
        "auth/too-many-requests"
      ) {
        errorMessage =
          "Too many requests. Please wait a few minutes and try again.";
      } else if (
        error.code ===
        "auth/invalid-phone-number"
      ) {
        errorMessage =
          "Invalid phone number format.";
      } else if (
        error.code ===
        "auth/quota-exceeded"
      ) {
        errorMessage =
          "SMS quota exceeded for today.";
      } else if (
        error.code ===
        "auth/app-not-authorized"
      ) {
        errorMessage =
          "Domain (localhost) not authorized in Firebase Console.";
      } else if (error.message) {
        errorMessage =
          error.message.replace(
            /^Firebase:\s*/,
            ""
          );
      }

      setMobileError(errorMessage);
    } finally {
      setSendingOTP(false);
    }
  };

  const verifyOTP = async () => {
    const code = otpInput.join("");

    if (code.length < 6) {
      setOtpError(
        "Enter the complete 6-digit code"
      );

      return;
    }

    if (!confirmationResultRef.current) {
      setOtpError(
        "Session expired. Please go back and resend OTP."
      );

      return;
    }

    stopWebOtp();
    setVerifyingOTP(true);
    setOtpError("");

    try {
      const result =
        await confirmationResultRef.current.confirm(
          code
        );

      const firebaseUser = result.user;

      const existingName =
        firebaseUser.displayName?.trim() || "";

      if (existingName) {

        const tableUserResponse =
          await authenticateTableUser(
            existingName,
            mobile,
            tableId
          );

        if (!tableUserResponse?.status) {
          throw new Error(
            tableUserResponse?.Message ||
            tableUserResponse?.message ||
            "Unable to login."
          );
        }

        if (
          !tableUserResponse?.token ||
          !storeAuthToken(
            tableUserResponse.token
          )
        ) {
          throw new Error(
            "Authentication token was not provided."
          );
        }

        setUser({
          fullName: existingName,
          mobileNumber: mobile,
        });

        showToast({
          type: "success",
          text1:
            `Welcome back, ${existingName}! 👋`,
        });

        setTimeout(goHome, 800);

        return;
      }

      setStep("register");
    } catch (error) {
      console.error(
        "Firebase confirm error:",
        error
      );

      if (
        error.code ===
        "auth/invalid-verification-code"
      ) {
        setOtpError(
          "Incorrect OTP code. Please try again."
        );
      } else if (
        error.code ===
        "auth/code-expired"
      ) {
        setOtpError(
          "OTP expired. Please go back and resend."
        );
      } else {
        setOtpError(
          error?.response?.data?.Message ||
          error?.response?.data?.message ||
          error?.message ||
          "Verification failed. Please try again."
        );
      }
    } finally {
      setVerifyingOTP(false);
    }
  };

  const saveAndLogin = async () => {
    if (
      !fullName.trim() ||
      fullName.trim().length < 3
    ) {
      setNameError(
        "Please enter a valid name (min 3 characters)"
      );

      return;
    }

    const cleanName = fullName.trim();

    const newUser = {
      fullName: cleanName,
      mobileNumber: mobile,
    };

    setNameError("");

    try {
      const tableUserResponse =
        await authenticateTableUser(
          cleanName,
          mobile,
          tableId
        );

      if (!tableUserResponse?.status) {
        throw new Error(
          tableUserResponse?.Message ||
          tableUserResponse?.message ||
          "Unable to register user."
        );
      }

      if (
        !tableUserResponse?.token ||
        !storeAuthToken(
          tableUserResponse.token
        )
      ) {
        throw new Error(
          "Authentication token was not provided."
        );
      }

      if (auth.currentUser) {
        await updateProfile(
          auth.currentUser,
          {
            displayName: cleanName,
          }
        );
      }

      setUser(newUser);

      showToast({
        type: "success",
        text1:
          `Welcome, ${newUser.fullName}! 🎉`,
      });

      setTimeout(goHome, 800);
    } catch (error) {
      console.error(
        "Table user registration error:",
        error
      );

      setNameError(
        error?.response?.data?.Message ||
        error?.response?.data?.message ||
        error?.message ||
        "Unable to register user. Please try again."
      );
    }
  };

  const tableNumber =
    tableData?.name || "-";

  const branchName =
    tableData?.store_name ||
    tableData?.branch_name ||
    tableData?.branchName ||
    tableData?.branch ||
    "Marstech Restaurant";

  return (
    <div
      className="h-screen w-full overflow-hidden relative selection:bg-emerald-500 selection:text-white"
      style={{
        backgroundImage:
          'url("/images/login-background.webp")',
        backgroundSize: "100% 100%",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundColor: "#f0fff8",
      }}
    >
      <div className="h-full w-full flex items-center justify-center">
        <div className="w-full max-w-md flex flex-col items-center justify-center px-4">
          <motion.div
            initial={{
              opacity: 0,
              y: -20,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.5,
              ease: "easeOut",
            }}
            className="relative z-10 flex flex-col items-center w-full"
          >
            <AnimatePresence>
              {toast && (
                <motion.div
                  initial={{
                    opacity: 0,
                    scale: 0.92,
                    y: -8,
                  }}
                  animate={{
                    opacity: 1,
                    scale: 1,
                    y: 0,
                  }}
                  exit={{
                    opacity: 0,
                    scale: 0.92,
                    y: -8,
                  }}
                  transition={{
                    duration: 0.3,
                    ease: [
                      0.16,
                      1,
                      0.3,
                      1,
                    ],
                  }}
                  className={`flex items-center gap-2.5 px-5 py-3 rounded-2xl shadow-lg mb-4 border ${toast.type === "success"
                      ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                      : "bg-rose-50 border-rose-200 text-rose-900"
                    }`}
                >
                  {toast.type === "success" ? (
                    <FaCheckCircle className="text-emerald-600 flex-shrink-0 text-lg" />
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-rose-500 text-white flex items-center justify-center flex-shrink-0 text-xs font-black">
                      !
                    </div>
                  )}

                  <div className="flex flex-col">
                    <span className="text-xs font-semibold tracking-wide">
                      {toast.text1}
                    </span>

                    {toast.text2 && (
                      <span className="text-[11px] font-semibold opacity-90 mt-0.5">
                        {toast.text2}
                      </span>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="relative mb-4 flex items-center justify-center">
              <img
                src="/images/m logo.webp"
                alt="Marstech Logo"
                className="w-24 h-24 object-contain drop-shadow-md"
                onError={(event) => {
                  event.target.style.display =
                    "none";
                }}
              />
            </div>

            <div className="flex flex-col items-center gap-2.5 w-full max-w-xs justify-center">
              <div className="glass-pill flex items-center gap-2.5 px-6 py-2 rounded bg-white/70 shadow-sm border border-[#22c55e]/30 w-full justify-start">
                <div className="bg-green-50 p-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    className="w-7 h-7 text-[#009447] flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M6 8h12" />
                    <path d="M4 4v7c0 1.1.9 2 2 2h3" />
                    <path d="M6 13v6" />
                    <path d="M9 13v6" />
                    <path d="M20 4v7c0 1.1-.9 2-2 2h-3" />
                    <path d="M18 13v6" />
                    <path d="M15 13v6" />
                    <path d="M6 11h3" />
                    <path d="M15 11h3" />
                  </svg>
                </div>

                <span className="text-base font-bold text-gray-600">
                  Table No :
                </span>

                {tableLoading ? (
                  <div className="w-8 h-4 bg-gray-200 animate-pulse rounded" />
                ) : (
                  <span className="text-sm font-black text-[#00713b]">
                    {tableNumber}
                  </span>
                )}
              </div>

              <div className="glass-pill flex items-center gap-2.5 px-6 py-2 rounded bg-white/90 shadow-sm border border-[#22c55e]/30 w-full justify-start">
                <div className="bg-green-50 p-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    className="w-7 h-7 text-[#009447] flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 9l2-5h12l2 5" />
                    <path d="M4 9c0 1.5 1.2 2.5 2.5 2.5S9 10.5 9 9c0 1.5 1.2 2.5 2.5 2.5S14 10.5 14 9c0 1.5 1.2 2.5 2.5 2.5S19 10.5 19 9" />
                    <path d="M5 11.5V20h14v-8.5" />
                    <path d="M9 20v-5h6v5" />
                    <path d="M3 20h18" />
                  </svg>
                </div>

                <span className="text-base font-bold text-gray-600">
                  Branch :
                </span>

                {tableLoading ? (
                  <div className="w-20 h-4 bg-gray-200 animate-pulse rounded" />
                ) : (
                  <span className="text-sm font-black text-[#00713b] truncate max-w-[150px]">
                    {branchName}
                  </span>
                )}
              </div>
            </div>
          </motion.div>

          <div className="relative z-10 w-full flex items-center justify-center mt-4">
            <motion.div
              initial={{
                opacity: 0,
                y: 20,
                scale: 0.97,
              }}
              animate={{
                opacity: 1,
                y: 0,
                scale: 1,
              }}
              transition={{
                duration: 0.4,
                ease: [
                  0.16,
                  1,
                  0.3,
                  1,
                ],
              }}
              className="w-full max-w-sm rounded-2xl p-5 sm:p-6 bg-white/95 shadow-xl shadow-green-950/5 border border-[#22c55e]/25"
            >
              <StepDots step={step} />

              <AnimatePresence mode="wait">
                {step === "mobile" && (
                  <MobileStep
                    key="mobile"
                    mobile={mobile}
                    setMobile={(value) => {
                      setMobile(value);
                      setMobileError("");
                    }}
                    mobileError={mobileError}
                    onSendOTP={sendOTP}
                    sendingOTP={sendingOTP}
                  />
                )}

                {step === "otp" && (
                  <OtpStep
                    key="otp"
                    mobile={mobile}
                    otpInput={otpInput}
                    otpError={otpError}
                    resendCountdown={resendCountdown}
                    expiryCountdown={expiryCountdown}
                    onOtpChange={handleOtpChange}
                    onVerify={verifyOTP}
                    onResend={sendOTP}
                    onBack={() => {
                      stopWebOtp();
                      setStep("mobile");
                      setOtpError("");
                    }}
                    verifyingOTP={verifyingOTP}
                  />
                )}

                {step === "register" && (
                  <RegisterStep
                    key="register"
                    fullName={fullName}
                    setFullName={(value) => {
                      setFullName(value);
                      setNameError("");
                    }}
                    nameError={nameError}
                    onSave={saveAndLogin}
                  />
                )}
              </AnimatePresence>
            </motion.div>
          </div>

          <motion.div
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            transition={{
              delay: 0.4,
            }}
            className="relative z-10 flex items-center justify-center text-[#3e3e3e] text-xs font-bold tracking-wide pt-5 pb-2 text-center px-4"
          >
            © 2025–2026 Marstech. All Rights
            Reserved.
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
